import { ProductAttrValueType } from "./ProductAttrValueType.mjs";
import { ProductAttrConditionEvaluator } from "./ProductAttrConditionEvaluator.mjs";
export class ProductAttrComputer {
    constructor(attrEvaluator = new ProductAttrConditionEvaluator()) {
        this.attrEvaluator = attrEvaluator;
        this.attributes = {};
        this.attributesFiltersMatched = {};
        this.attributesPreferred = {};
        this.attributesOutOfStock = {};
        this.attributesConstrained = {};
        this.attributesSuggested = {};
        this.attributesFiltered = {};
        this.useFilters = true;
    }
    reset(attributes) {
        this.attributes = attributes;
    }
    getAttributes() {
        return this.attributes;
    }
    /**
     * Evaluates filtered, constrained, suggested and unavailable values.
     *
     * @param productItem
     */
    evaluate(productItem, map, useFilters) {
        if (useFilters !== undefined) {
            this.useFilters = useFilters;
        }
        if (map) {
            this.reset(map);
            this.attrEvaluator.reset(map);
        }
        this.evaluateFilteredValues(productItem);
        this.evaluateConstrainedValues(productItem);
        this.evaluateSuggestedValues();
        this.evaluateOutOfStockValues();
    }
    evaluateFilteredValues(productItem) {
        this.attributesFiltered = {};
        this.attributesFiltersMatched = {};
        for (const attributeName in this.attributes) {
            const [attrValues, filters] = this.attrEvaluator.filter(productItem.toTestableOneDimensionalArray(), attributeName);
            this.attributesFiltered[attributeName] = attrValues;
            this.attributesFiltersMatched[attributeName] = filters.length;
        }
    }
    evaluateConstrainedValues(productItem) {
        this.attrEvaluator.clearDebug();
        this.attributesConstrained = {};
        for (const attributeName in this.attributes) {
            this.attributesConstrained[attributeName] = [];
            for (const attributeValue of this.getAllValues(attributeName)) {
                const result = this.attrEvaluator.canIChangeAttribute(productItem.toTestableOneDimensionalArray(), attributeName, String(attributeValue));
                if (!result) {
                    this.attributesConstrained[attributeName].push(attributeValue);
                }
            }
        }
    }
    evaluateSuggestedValues() {
        this.attributesSuggested = {};
        for (const attributeName in this.attributes) {
            let values = this.getFilteredValues(attributeName);
            if (!this.useFilters || !values.length) {
                values = this.getAllValues(attributeName);
            }
            let preferredValue = this.getPreferredValue(attributeName);
            if (preferredValue !== null) {
                if (!Array.isArray(preferredValue)) {
                    preferredValue = [preferredValue];
                }
                preferredValue = preferredValue.filter((value) => !this.isConstrained(attributeName, value) && this.canValueBe(attributeName, value) && values.indexOf(value) < 0);
                values = values.concat(preferredValue);
            }
            this.attributesSuggested[attributeName] = values;
        }
    }
    evaluateOutOfStockValues() {
        this.attributesOutOfStock = {};
        for (const attributeName in this.attributes) {
            const values = this.attributes[attributeName].outOfStockValues;
            if (values.length) {
                this.attributesOutOfStock[attributeName] = values;
            }
        }
    }
    /**
     * Determines whether the value is suggested and not constrained.
     *
     * @param attributeName The name of the attribute.
     * @param attributeValue The value of the attribute.
     */
    isAvailable(attributeName, attributeValue) {
        return (!this.isConstrained(attributeName, attributeValue) &&
            (this.isInSuggestedValues(attributeName, attributeValue) || this.isDynamicValue(attributeName)));
    }
    /**
     * Gets the first suggested value that isn't constrained.
     *
     * @param attributeName The name of the attribute.
     * @returns The first suggested value that isn't constrained, or null if no such value exists. For multi-value attributes, an empty array is returned.
     */
    getDefaultValue(attributeName) {
        if (this.isMultiValue(attributeName)) {
            return [];
        }
        const filteredValues = this.getFilteredValues(attributeName).filter((value) => !this.isConstrained(attributeName, value));
        if (filteredValues.length) {
            return filteredValues[0];
        }
        const suggestedValues = this.getSuggestedValues(attributeName).filter((value) => !this.isConstrained(attributeName, value));
        if (suggestedValues.length) {
            return suggestedValues[0];
        }
        return null;
    }
    /**
     * Retrieves all values related to the attribute.
     *
     * @param attributeName The name of the attribute.
     * @returns An array of all values related to the attribute.
     */
    getAllValues(attributeName) {
        if (this.isSupported(attributeName)) {
            const attrValuesRaw = Object.keys(this.attributes[attributeName].valuesAndConstraints);
            return attrValuesRaw.map((attrValueRaw) => { var _a; return (_a = this.parseAttribute(attributeName, attrValueRaw)) !== null && _a !== void 0 ? _a : attrValueRaw; });
        }
        return [];
    }
    /**
     * Iterates through suggested values and returns the highest non-constrained number.
     *
     * @param attributeName The name of the attribute.
     * @returns A non-constrained number if found, null otherwise.
     */
    getHighestAvailableValue(attributeName) {
        const values = this.getSuggestedValues(attributeName).filter((value) => !this.isConstrained(attributeName, value));
        const highestValue = Math.max(...values.map((value) => parseFloat(String(value))));
        return isNaN(highestValue) ? null : highestValue;
    }
    /**
     * Iterates through suggested values and returns the lowest non-constrained number.
     *
     * @param attributeName The name of the attribute.
     * @returns A non-constrained number if found, null otherwise.
     */
    getLowestAvailableValue(attributeName) {
        const values = this.getSuggestedValues(attributeName).filter((value) => !this.isConstrained(attributeName, value));
        const lowestValue = Math.min(...values.map((value) => parseFloat(String(value))));
        return isNaN(lowestValue) ? null : lowestValue;
    }
    /**
     * Suggested values are filtered values, but if filtered values are empty or useFilters is set to false, it will fallback to all values.
     *
     * OBS! Can contain constrained values.
     *
     * @param attributeName The name of the attribute.
     * @returns An array of suggested values.
     */
    getSuggestedValues(attributeName) {
        if (this.attributesSuggested.hasOwnProperty(attributeName)) {
            return this.attributesSuggested[attributeName];
        }
        return [];
    }
    /**
     * Retrieves filtered values.
     *
     * @param attributeName The name of the attribute.
     * @returns An array of filtered values.
     */
    getFilteredValues(attributeName) {
        if (this.attributesFiltered.hasOwnProperty(attributeName)) {
            return this.attributesFiltered[attributeName];
        }
        return [];
    }
    /**
     * Determines whether the attribute has been filtered.
     *
     * @param attributeName The name of the attribute.
     * @returns True if the attribute has been filtered, false otherwise.
     */
    hasBeenFiltered(attributeName) {
        if (this.attributesFiltersMatched.hasOwnProperty(attributeName)) {
            return this.attributesFiltersMatched[attributeName] > 0;
        }
        return false;
    }
    hasPreferredValue(attributeName) {
        return this.attributesPreferred.hasOwnProperty(attributeName);
    }
    removePreferredValue(attributeName, attributeValue) {
        const preferredValue = this.getPreferredValue(attributeName);
        if (this.isMultiValue(attributeName) && Array.isArray(preferredValue)) {
            this.setPreferredValue(attributeName, preferredValue.filter((value) => value != attributeValue));
        }
        else {
            delete this.attributesPreferred[attributeName];
        }
    }
    getPreferredValue(attributeName) {
        if (this.hasPreferredValue(attributeName)) {
            return this.attributesPreferred[attributeName];
        }
        return null;
    }
    /**
     * Sets a preferred value. Will be available as a suggested value if not constrained.
     *
     * @param attributeName The name of the attribute.
     * @param attributeValue The value of the attribute.
     * @returns void
     */
    setPreferredValue(attributeName, attributeValue) {
        this.attributesPreferred[attributeName] = attributeValue;
    }
    setPreferredValues(attributes) {
        this.attributesPreferred = attributes;
    }
    /**
     * Retrieves constrained values related to an attribute.
     *
     * @param attributeName The name of the attribute.
     * @returns An array of constrained values.
     */
    getConstrainedValues(attributeName) {
        if (this.attributesConstrained.hasOwnProperty(attributeName)) {
            return this.attributesConstrained[attributeName];
        }
        return [];
    }
    getOutOfStockValues(attributeName) {
        if (this.attributesOutOfStock.hasOwnProperty(attributeName)) {
            return this.attributesOutOfStock[attributeName];
        }
        return [];
    }
    getIcons(attributeName) {
        var _a, _b;
        return (_b = (_a = this.attributes[attributeName]) === null || _a === void 0 ? void 0 : _a.icons) !== null && _b !== void 0 ? _b : {};
    }
    /**
     * Determines whether the attribute is supported.
     *
     * @param attributeName The name of the attribute.
     * @returns True if the attribute is supported, false otherwise.
     */
    isSupported(attributeName) {
        return this.attributes.hasOwnProperty(attributeName);
    }
    /**
     * Determines whether the attribute is required.
     *
     * @param attributeName The name of the attribute.
     * @returns True if the attribute is required, false otherwise.
     */
    isRequired(attributeName) {
        var _a;
        return this.isSupported(attributeName) && ((_a = this.attributes[attributeName]) === null || _a === void 0 ? void 0 : _a.isRequired);
    }
    /**
     * Determines whether the attribute supports multiple values.
     *
     * @param attributeName The name of the attribute.
     * @returns True if the attribute supports multiple values, false otherwise.
     */
    isMultiValue(attributeName) {
        var _a;
        return this.isSupported(attributeName) && ((_a = this.attributes[attributeName]) === null || _a === void 0 ? void 0 : _a.isMultiValue);
    }
    /**
     * Determines whether the attribute is a dynamic value.
     *
     * @param attributeName The name of the attribute.
     * @returns True if the attribute is a dynamic value, false otherwise.
     */
    isDynamicValue(attributeName) {
        var _a;
        return this.isSupported(attributeName) && ((_a = this.attributes[attributeName]) === null || _a === void 0 ? void 0 : _a.isDynamicValue);
    }
    /**
     * Retrieves the value type of the attribute.
     *
     * @param attributeName The name of the attribute.
     * @returns A ProductAttrValueType ('string', 'boolean', 'integer' or 'float'), or null if the attribute does not exist.
     */
    getValueType(attributeName) {
        var _a, _b;
        return (_b = (_a = this.attributes[attributeName]) === null || _a === void 0 ? void 0 : _a.valueType) !== null && _b !== void 0 ? _b : null;
    }
    /**
     * Determines whether the attribute is of type boolean.
     *
     * @param attributeName The name of the attribute.
     * @returns True if the attribute is of type boolean, false otherwise.
     */
    isBooleanType(attributeName) {
        return this.getValueType(attributeName) === ProductAttrValueType.BOOL;
    }
    /**
     * Determines whether the attribute is of type integer.
     *
     * @param attributeName The name of the attribute.
     * @returns True if the attribute is of type integer, false otherwise.
     */
    isIntType(attributeName) {
        return this.getValueType(attributeName) === ProductAttrValueType.INT;
    }
    /**
     * Determines whether the attribute is of type float.
     *
     * @param attributeName The name of the attribute.
     * @returns True if the attribute is of type float, false otherwise.
     */
    isFloatType(attributeName) {
        return this.getValueType(attributeName) === ProductAttrValueType.FLOAT;
    }
    /**
     * Determines whether the attribute is of type string.
     *
     * @param attributeName The name of the attribute.
     * @returns True if the attribute is of type string, false otherwise.
     */
    isStringType(attributeName) {
        return this.getValueType(attributeName) === ProductAttrValueType.STRING;
    }
    /**
     * Determines whether the attribute is of type number (float or integer).
     *
     * @param attributeName The name of the attribute.
     * @returns True if the attribute is of type number, false otherwise.
     */
    isNumberType(attributeName) {
        return this.isIntType(attributeName) || this.isFloatType(attributeName);
    }
    /**
     * Checks if a given attribute value is present in the list of filtered values for a specified attribute.
     *
     * @param attributeName The name of the attribute.
     * @param attributeValue The value of the attribute.
     * @returns True if the attribute value is in the list of filtered values for the specified attribute, false otherwise.
     */
    isInFilteredValues(attributeName, attributeValue) {
        return this.getFilteredValues(attributeName).includes(attributeValue);
    }
    /**
     * Checks if a given attribute value is present in the list of suggested values for a specified attribute.
     *
     * @param attributeName The name of the attribute.
     * @param attributeValue The value of the attribute.
     * @returns True if the attribute value is in the list of suggested values for the specified attribute, false otherwise.
     */
    isInSuggestedValues(attributeName, attributeValue) {
        return this.getSuggestedValues(attributeName).includes(attributeValue);
    }
    /**
     * Checks if a given attribute value is present in the list of constrained values for a specified attribute.
     *
     * @param attributeName The name of the attribute.
     * @param attributeValue The value of the attribute.
     * @returns True if the attribute value is in the list of constrained values for the specified attribute, false otherwise.
     */
    isConstrained(attributeName, attributeValue) {
        return this.getConstrainedValues(attributeName).includes(attributeValue);
    }
    isOutOfStock(attributeName, attributeValue) {
        return this.getOutOfStockValues(attributeName).includes(attributeValue);
    }
    /**
     * Determines whether the attribute is recommended, i.e. not constrained, in stock and in the list of filtered values.
     *
     * @param attributeName The name of the attribute.
     * @param attributeValue The value of the attribute.
     * @returns True if the attribute is recommended, false otherwise.
     */
    isRecommended(attributeName, attributeValue) {
        if (this.isConstrained(attributeName, attributeValue) || this.isOutOfStock(attributeName, attributeValue)) {
            return false;
        }
        return this.hasBeenFiltered(attributeName) && this.isInFilteredValues(attributeName, attributeValue);
    }
    canValueBe(attributeName, attributeValue) {
        var _a;
        if (!this.isDynamicValue(attributeName)) {
            return (_a = this.attributes[attributeName]) === null || _a === void 0 ? void 0 : _a.valuesAndConstraints.hasOwnProperty(attributeValue.toString());
        }
        return true;
    }
    /**
     * Parses an attribute value to the correct type.
     *
     * @param attributeName The name of the attribute.
     * @param attributeValueRaw The raw attribute value.
     * @returns The parsed attribute value on success, null on failure.
     */
    parseAttribute(attributeName, attributeValueRaw) {
        if (Array.isArray(attributeValueRaw)) {
            const parsedValues = [];
            for (const value of attributeValueRaw) {
                const parsedValue = this.parseAttribute(attributeName, value);
                if (parsedValue !== null) {
                    parsedValues.push(parsedValue);
                }
            }
            return parsedValues.length > 0 ? parsedValues : null;
        }
        if (!['string', 'boolean', 'number'].includes(typeof attributeValueRaw)) {
            return null;
        }
        if (this.isStringType(attributeName)) {
            return String(attributeValueRaw);
        }
        else if (this.isBooleanType(attributeName)) {
            try {
                const value = JSON.parse(attributeValueRaw);
                if (typeof value === 'boolean') {
                    return value;
                }
            }
            catch (e) { }
        }
        else if (this.isIntType(attributeName)) {
            const value = parseInt(attributeValueRaw);
            return isNaN(value) ? null : value;
        }
        else if (this.isFloatType(attributeName)) {
            const value = parseFloat(attributeValueRaw);
            return isNaN(value) ? null : value;
        }
        return null;
    }
    /* SET AND GET */
    get debugList() {
        return this.attrEvaluator.debugList;
    }
    set debugEnabled(value) {
        this.attrEvaluator.debugEnabled = value;
    }
    get debugEnabled() {
        return this.attrEvaluator.debugEnabled;
    }
}
